import { RefObject, useRef } from 'react'
import dynamic from 'next/dynamic'
import Image from 'next/legacy/image'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import styled from 'styled-components'

import structure from '@/structure'
import { breakpoints, colors } from '@/styles'
import useMediaQuery from '@/hooks/use-media-query'
import { makeMainLayout } from '@/layouts/main'
import GeneralFAQ from '@/features/Faq/general'
import heroImage from '@/features/Faq/images/hero@3x.png'
import LandlordFAQ from '@/features/Faq/landlords'
import RenterFAQ from '@/features/Faq/renters'
import { HeaderTheme } from '@/features/Header'
import ApplicationDownload from '@/components/application-download'
import Head from '@/components/head'

const CitySelector = dynamic(() => import('@/features/CitySelector'))

const Wrapper = styled.main`
  display: flex;
  width: 100%;
  justify-content: center;
  flex-direction: column;
  background-color: ${colors.white};
  background-repeat: no-repeat;
`

const SearchWrapper = styled.div`
  background-color: ${colors.background};
  z-index: 402;
  padding: 0 25px;
  padding-top: ${structure.search.height - structure.header.height}px;

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    padding-top: ${structure.search.heightMobile -
    structure.header.heightMobile}px;
  }
`

const TopSection = styled.section`
  display: flex;
  width: 100%;
  min-height: 520px;
  display: flex;
  position: relative;
  background-color: ${colors.background};
  z-index: 1;
  overflow: hidden;
`

const BackgroundWrapper = styled.div`
  position: absolute;
  height: 100%;
  width: 100%;
  z-index: -1;

  > span > img {
    background-repeat: no-repeat;
  }
`

const padding = {
  big: 0.4,
  small: 0.35
}

const TopContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
  padding: 0 20px calc(100vw * ${padding.big});

  @media (max-width: 1300px) {
    padding-bottom: 550px;
  }

  @media (max-width: ${breakpoints.tabletLandscape}) {
    padding-bottom: calc(100vw * ${padding.small});
  }

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    padding-bottom: 320px;
  }

  @media (max-width: ${breakpoints.phoneMax}) {
    padding-bottom: 200px;
  }
`

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-self: center;
  width: 100%;
`

const LandlordWrapper = styled.div`
  background-color: ${colors.background};
`

const ContentWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  margin: 150px auto;
  padding: 0 20px;
  width: 100%;
  max-width: 880px;

  @media (max-width: ${breakpoints.tabletLandscape}) {
    max-width: 680px;
  }

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    align-items: flex-start;
    margin: 80px auto;
  }
`

const Title = styled.h1`
  width: 710px;
  font-size: 81px;
  font-family: futura-pt-bold, Futura, sans-serif;
  letter-spacing: -0.17px;
  line-height: 86px;
  text-align: center;
  font-weight: 700;
  z-index: 3;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    font-size: 57px;
    width: 100%;
    line-height: 54px;
    letter-spacing: -0.1px;
  }
`

const Category = styled.div`
  display: flex;
  z-index: 3;
  position: relative;
  margin-top: 32px;

  button:nth-child(2) {
    margin: 0px 30px;
  }

  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    flex-direction: column;
    button:nth-child(2) {
      margin: 25px 0px;
    }
  }
`

const CategoryButton = styled.button`
  box-sizing: 'border-box';
  height: 48px;
  width: 254px;
  border: 2px solid #d8d8d8;
  border-radius: 24px;
  text-align: 'center';
  background-color: transparent;
  font-size: 19px;
  font-family: 'Open Sans', sans-serif;
  font-weight: bold;
  letter-spacing: -0.2px;
`

const SectionTitle = styled.h2`
  font-size: 50px;
  font-weight: 800;
  letter-spacing: -1.25px;
  line-height: 54px;
  color: ${colors.regular};
  margin: 0;

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    font-size: 30px;
    letter-spacing: -0.64px;
    line-height: 34px;
    margin-bottom: 20px;
  }
`

const PageContainer = styled.div`
  width: 100%;

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    width: 100%;
  }
`

const Faq = () => {
  const { t } = useTranslation()
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const skipToGeneralFAQ = useRef<HTMLDivElement>(null)
  const skipToLandlordsFAQ = useRef<HTMLDivElement>(null)
  const skipToRentersFAQ = useRef<HTMLDivElement>(null)

  const onSkipToFAQ = (skipToFAQ: RefObject<HTMLDivElement>) => {
    if (skipToFAQ.current) {
      window.scrollTo({
        top: skipToFAQ.current.offsetTop - 100,
        left: 0,
        behavior:
          'scrollBehavior' in document.documentElement.style ? 'smooth' : 'auto'
      })
    }
  }

  const metaTitle = t(
    'faq:Renters & Landlords FAQ | liv.rent - Apartments & Homes for Rent'
  )
  const metaDescription = t(
    `faq:Canada's trusted rental resources for landlords & renters. Find useful information in understanding your rights and responsibilities as a landlord or tenant.`
  )

  return (
    <Wrapper>
      <Head title={metaTitle} description={metaDescription} path="/faq" />
      <SearchWrapper>
        {displayMobile && (
          <CitySelector instanceId="faq-select-city" id="faq-select-city" />
        )}
      </SearchWrapper>
      <TopSection>
        <BackgroundWrapper>
          <Image
            src={heroImage}
            alt=""
            layout="fill"
            objectFit="contain"
            objectPosition="center bottom"
            placeholder="blur"
          />
        </BackgroundWrapper>
        <TopContentContainer>
          <Title>{t('faq:In case you missed anything.')}</Title>
          <Category>
            <CategoryButton
              id="faq-general-click"
              onClick={() => onSkipToFAQ(skipToGeneralFAQ)}
            >
              {t('faq:General')}
            </CategoryButton>
            <CategoryButton
              id="faq-landlords-click"
              onClick={() => onSkipToFAQ(skipToLandlordsFAQ)}
            >
              {t('faq:Landlords')}
            </CategoryButton>
            <CategoryButton
              id="faq-renters-click"
              onClick={() => onSkipToFAQ(skipToRentersFAQ)}
            >
              {t('faq:Renters')}
            </CategoryButton>
          </Category>
        </TopContentContainer>
      </TopSection>
      <PageWrapper>
        <ContentWrapper ref={skipToGeneralFAQ}>
          <SectionTitle>{t('faq:General')}</SectionTitle>
          <PageContainer>
            <GeneralFAQ />
          </PageContainer>
        </ContentWrapper>
        <LandlordWrapper>
          <ContentWrapper ref={skipToLandlordsFAQ}>
            <SectionTitle>{t('faq:Landlords')}</SectionTitle>
            <PageContainer>
              <LandlordFAQ />
            </PageContainer>
          </ContentWrapper>
        </LandlordWrapper>
        <ContentWrapper ref={skipToRentersFAQ}>
          <SectionTitle>{t('faq:Renters')}</SectionTitle>
          <PageContainer>
            <RenterFAQ />
          </PageContainer>
        </ContentWrapper>
      </PageWrapper>
      <ApplicationDownload />
    </Wrapper>
  )
}

export const getStaticProps = async ({ locale }: { locale: string }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale, ['translation', 'faq']))
    }
  }
}

const headerConfig = {
  showSearch: false,
  showNavItems: true,
  theme: HeaderTheme.TRANSPARENT
}

Faq.Layout = makeMainLayout({ headerConfig, background: colors.background })

export default Faq
